// conectagran/frontend/src/pages/Adskeeper/index.tsx
import { useMemo, useState, type ReactNode } from 'react'
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { Link2, Loader2, RefreshCw, TrendingUp, Unplug } from 'lucide-react'
import {
  useAdskeeperConnection,
  useAdskeeperReport,
  useDeleteAdskeeperConnection,
  useSaveAdskeeperConnection,
  type AdskeeperMetrics,
  type AdskeeperWidgetRow,
  type AdskeeperDomainRow,
} from '@/api/hooks/useAdskeeper'

// ─── Helpers de data (ancorado em America/Sao_Paulo) ────────────────────────
function brtDate(offsetDays = 0): string {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
  const base = fmt.format(new Date()) // YYYY-MM-DD em BRT
  const d = new Date(`${base}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + offsetDays)
  return d.toISOString().slice(0, 10)
}

const fmtInt = (n: number) => new Intl.NumberFormat('pt-BR').format(Math.round(n || 0))
const fmtMoney = (n: number) => `$${(n || 0).toFixed(2)}`
const fmtPct = (n: number) => `${(n || 0).toFixed(1)}%`
const fmtCents = (n: number) => `${(n || 0).toFixed(1)}¢`
const fmtDayLabel = (d: string) => d.slice(8, 10) + '/' + d.slice(5, 7)

type Preset = 'today' | 'yesterday' | '7d' | '30d' | 'custom'

const PRESETS: { id: Preset; label: string }[] = [
  { id: 'today', label: 'Hoje' },
  { id: 'yesterday', label: 'Ontem' },
  { id: '7d', label: '7 dias' },
  { id: '30d', label: '30 dias' },
  { id: 'custom', label: 'Custom' },
]

function rangeFor(preset: Preset, customFrom: string, customTo: string): { from: string; to: string } {
  switch (preset) {
    case 'today': return { from: brtDate(0), to: brtDate(0) }
    case 'yesterday': return { from: brtDate(-1), to: brtDate(-1) }
    case '7d': return { from: brtDate(-6), to: brtDate(0) }
    case '30d': return { from: brtDate(-29), to: brtDate(0) }
    case 'custom': return { from: customFrom || brtDate(0), to: customTo || brtDate(0) }
  }
}

// ─── KPI card ───────────────────────────────────────────────────────────────
function Kpi({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-foreground">{value}</div>
      {hint && <div className="mt-0.5 text-xs text-muted-foreground">{hint}</div>}
    </div>
  )
}

// ─── Tabela genérica de métricas ──────────────────────────────────────────────
function MetricsTable<T extends AdskeeperMetrics>({
  title, rows, firstCol, render,
}: {
  title: string
  rows: T[]
  firstCol: string
  render: (r: T) => ReactNode
}) {
  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="border-b border-border px-4 py-3 text-sm font-medium text-foreground">{title}</div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-muted-foreground">
              <th className="px-4 py-2 font-medium">{firstCol}</th>
              <th className="px-4 py-2 text-right font-medium">Impr.</th>
              <th className="px-4 py-2 text-right font-medium">CTR</th>
              <th className="px-4 py-2 text-right font-medium">CPC</th>
              <th className="px-4 py-2 text-right font-medium">eCPM</th>
              <th className="px-4 py-2 text-right font-medium">Receita</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">Sem dados no período.</td></tr>
            )}
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-border/60">
                <td className="px-4 py-2 text-foreground">{render(r)}</td>
                <td className="px-4 py-2 text-right tabular-nums">{fmtInt(r.impressions)}</td>
                <td className="px-4 py-2 text-right tabular-nums">{fmtPct(r.ctr)}</td>
                <td className="px-4 py-2 text-right tabular-nums">{fmtCents(r.cpc)}</td>
                <td className="px-4 py-2 text-right tabular-nums">{fmtMoney(r.ecpm)}</td>
                <td className="px-4 py-2 text-right font-medium tabular-nums text-emerald-400">{fmtMoney(r.wages)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─── Tela de conexão (cada user configura a conta dele) ──────────────────────
function ConnectScreen() {
  const [clientId, setClientId] = useState('')
  const [token, setToken] = useState('')
  const save = useSaveAdskeeperConnection()

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-4">
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-6">
        <h1 className="flex items-center gap-2 text-lg font-semibold text-foreground">
          <Link2 className="h-5 w-5" /> Conectar Adskeeper
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Use a sua própria conta. Pegue o <strong>Client ID</strong> e o <strong>Token de acesso</strong> em
          {' '}<em>API / Account info</em> no painel do Adskeeper.
        </p>

        <div className="mt-4 space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground">Client ID</label>
            <input
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder="ex: 859805"
              className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground">Token de acesso</label>
            <input
              value={token}
              onChange={(e) => setToken(e.target.value)}
              type="password"
              placeholder="seu token (32 caracteres)"
              className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
            />
          </div>

          {save.isError && (
            <div className="rounded-md border border-red-500/40 bg-red-500/10 p-2 text-xs text-red-300">
              {(save.error as Error)?.message || 'Falha ao validar as credenciais.'}
            </div>
          )}

          <button
            disabled={!clientId.trim() || !token.trim() || save.isPending}
            onClick={() => save.mutate({ client_id: clientId.trim(), token: token.trim() })}
            className="flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {save.isPending ? 'Validando…' : 'Conectar'}
          </button>
          <p className="text-xs text-muted-foreground">
            Validamos o token antes de salvar. Ele fica guardado só na sua conta.
          </p>
        </div>
      </div>
    </div>
  )
}

export function Adskeeper() {
  const conn = useAdskeeperConnection()
  const disconnect = useDeleteAdskeeperConnection()

  const [preset, setPreset] = useState<Preset>('today')
  const [customFrom, setCustomFrom] = useState(brtDate(-6))
  const [customTo, setCustomTo] = useState(brtDate(0))

  const range = useMemo(() => rangeFor(preset, customFrom, customTo), [preset, customFrom, customTo])
  const connected = conn.data?.connected === true
  const { data, isLoading, isError, error, refetch, isFetching } = useAdskeeperReport({ ...range, enabled: connected })

  if (conn.isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }
  if (!connected) {
    return <ConnectScreen />
  }

  const t: AdskeeperMetrics = data?.totals ?? { impressions: 0, clicks: 0, wages: 0, ctr: 0, cpc: 0, ecpm: 0 }
  const chart = (data?.byDay ?? []).map((d) => ({ label: fmtDayLabel(d.date), receita: d.wages, cliques: d.clicks }))

  return (
    <div className="space-y-4 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold text-foreground">
            <TrendingUp className="h-5 w-5" /> Adskeeper
          </h1>
          <p className="text-xs text-muted-foreground">
            Dia fechado 00:00–23:59 (America/São_Paulo) · {range.from === range.to ? range.from : `${range.from} → ${range.to}`}
            {conn.data?.client_id && <span className="ml-1">· conta {conn.data.client_id}</span>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              onClick={() => setPreset(p.id)}
              className={`rounded-md px-3 py-1.5 text-sm transition ${
                preset === p.id
                  ? 'bg-primary text-primary-foreground'
                  : 'border border-border bg-card text-muted-foreground hover:text-foreground'
              }`}
            >
              {p.label}
            </button>
          ))}
          <button
            onClick={() => refetch()}
            className="flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} /> Atualizar
          </button>
          <button
            onClick={() => { if (confirm('Desconectar a conta Adskeeper?')) disconnect.mutate() }}
            className="flex items-center gap-1.5 rounded-md border border-red-500/40 bg-card px-3 py-1.5 text-sm text-red-300 hover:bg-red-500/10"
          >
            <Unplug className="h-3.5 w-3.5" /> Desconectar
          </button>
        </div>
      </div>

      {/* Custom date inputs */}
      {preset === 'custom' && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">De</span>
          <input
            type="date"
            value={customFrom}
            onChange={(e) => setCustomFrom(e.target.value)}
            className="rounded-md border border-border bg-card px-2 py-1 text-foreground"
          />
          <span className="text-muted-foreground">até</span>
          <input
            type="date"
            value={customTo}
            onChange={(e) => setCustomTo(e.target.value)}
            className="rounded-md border border-border bg-card px-2 py-1 text-foreground"
          />
        </div>
      )}

      {isError && (
        <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-300">
          {(error as Error)?.message || 'Falha ao carregar o relatório do Adskeeper.'}
        </div>
      )}

      {/* Alerta de risco de ban por CTR */}
      {!isLoading && data && t.impressions > 50 && (
        t.ctr > 25 ? (
          <div className="rounded-lg border border-red-500/50 bg-red-500/10 p-4 text-sm text-red-200">
            🚨 <strong>CTR em {t.ctr.toFixed(1)}% — risco ALTO de ban.</strong> Acima de ~25% o algoritmo do Adskeeper marca como clique inválido. <strong>Baixe o passthrough do overlay agora</strong> e saque o saldo do dia.
          </div>
        ) : t.ctr >= 20 ? (
          <div className="rounded-lg border border-amber-500/50 bg-amber-500/10 p-4 text-sm text-amber-200">
            ⚠️ <strong>CTR em {t.ctr.toFixed(1)}% — perto do limite.</strong> Zona de atenção. Considere reduzir o passthrough antes de passar de 25%.
          </div>
        ) : (
          <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-4 text-sm text-emerald-200">
            ✓ <strong>CTR em {t.ctr.toFixed(1)}% — dentro do seguro.</strong> Padrão sustentável; a conta tende a durar.
          </div>
        )
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <Kpi label="Impressões" value={isLoading ? '—' : fmtInt(t.impressions)} />
        <Kpi label="Cliques" value={isLoading ? '—' : fmtInt(t.clicks)} />
        <Kpi label="CTR" value={isLoading ? '—' : fmtPct(t.ctr)} />
        <Kpi label="CPC" value={isLoading ? '—' : fmtCents(t.cpc)} />
        <Kpi label="eCPM" value={isLoading ? '—' : fmtMoney(t.ecpm)} hint="receita ÷ impressões" />
        <Kpi label="Receita" value={isLoading ? '—' : fmtMoney(t.wages)} />
      </div>

      {/* Gráfico por dia */}
      <div className="rounded-lg border border-border bg-card p-4">
        <div className="mb-2 text-sm font-medium text-foreground">Receita por dia</div>
        <div className="h-56 w-full">
          {chart.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              {isLoading ? 'Carregando…' : 'Sem dados no período.'}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chart} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                <defs>
                  <linearGradient id="ak-rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#34d399" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#34d399" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="label" tick={{ fill: '#9ca3af', fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} tickLine={false} axisLine={false} width={40} />
                <Tooltip
                  contentStyle={{ background: '#111827', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ color: '#e5e7eb' }}
                  formatter={(v: number, name: string) => name === 'receita' ? [fmtMoney(v), 'Receita'] : [fmtInt(v), 'Cliques']}
                />
                <Area type="monotone" dataKey="receita" stroke="#34d399" strokeWidth={2} fill="url(#ak-rev)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Tabelas */}
      <div className="grid gap-4 xl:grid-cols-2">
        <MetricsTable<AdskeeperWidgetRow>
          title="Por widget"
          firstCol="Widget"
          rows={data?.byWidget ?? []}
          render={(r) => (
            <span>
              <span className="font-medium">{r.widgetId}</span>
              {r.name && <span className="ml-1 text-xs text-muted-foreground">{r.name}</span>}
            </span>
          )}
        />
        <MetricsTable<AdskeeperDomainRow>
          title="Por domínio"
          firstCol="Domínio"
          rows={data?.byDomain ?? []}
          render={(r) => <span className="font-medium">{r.domain}</span>}
        />
      </div>
    </div>
  )
}
